import React, { useState, useEffect, useMemo, startTransition } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Pagination,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  ArrowForward as ArrowForwardIcon,
  Folder as FolderIcon,
  HourglassEmpty as HourglassIcon,
  CheckCircle as CheckCircleIcon,
  GridView as GridViewIcon,
  List as ListIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ProjectAdminSummary, ProjectStatusEnum } from 'src/api/Api';
import clientService from 'src/clientService';
import { useAppState } from 'src/state-management/state-provider';
import { formatDate } from 'src/util/date-utils';
import { countryList } from 'src/constants/countryList';
import ProjectDialog from 'src/view/components/ProjectDialog';

/**
 * Returns consistent chip styles and label for a project's status.
 * - Completed (end date in past) → Green
 * - Under Development              → Orange
 * - Draft (default)                → Grey
 */
const getStatusStyle = (
  project_status: string | null | undefined,
  project_end_date: string | null | undefined
): { backgroundColor: string; color: string; label: string } => {
  const isCompleted = project_end_date && new Date(project_end_date) < new Date();
  if (isCompleted) {
    return { backgroundColor: 'rgba(16, 185, 129, 0.85)', color: '#FFF', label: 'Completed' };
  }
  if (project_status === ProjectStatusEnum.UnderDevelopment) {
    return { backgroundColor: 'rgba(249, 115, 22, 0.85)', color: '#FFF', label: 'Under Development' };
  }
  // Draft or unknown
  return { backgroundColor: 'rgba(107, 114, 128, 0.7)', color: '#FFF', label: project_status || 'Draft' };
};

const ProjectList: React.FC = () => {
  const { state, dispatch } = useAppState();
  const projects = state.admin.projects;
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const rowsPerPage = 6;

  const [selectedOrg, setSelectedOrg] = useState('All Organisations');
  const [selectedStatus, setSelectedStatus] = useState('All Status');
  const [sortBy, setSortBy] = useState('Latest');
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);

  const [orgAnchorEl, setOrgAnchorEl] = useState<null | HTMLElement>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);

  const organisations = useMemo(() => {
    const orgs = new Set(projects.map(p => p.project_developer).filter(Boolean));
    return ['All Organisations', ...Array.from(orgs)] as string[];
  }, [projects]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await clientService.api.getAllProjectsAdminApiV1AdminProjectsGet();
        startTransition(() => {
          dispatch({ type: 'ADMIN_SET_PROJECTS', payload: response.data });
        });
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };

    fetchProjects();
  }, []);

  const stats = useMemo(() => {
    const total = projects.length;
    const now = new Date();
    
    // In Progress: Explicitly Under Development
    const inProgress = projects.filter(p => 
      p.project_status === ProjectStatusEnum.UnderDevelopment
    ).length;
    
    // Completed: End date has passed
    const completed = projects.filter(p => {
      if (!p.project_end_date) return false;
      return new Date(p.project_end_date) < now;
    }).length;
    
    return { total, inProgress, completed };
  }, [projects]);

  const filteredProjects = useMemo(() => {
    let result = projects.filter(p => 
      p.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.project_developer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.country?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (selectedOrg !== 'All Organisations') {
      result = result.filter(p => p.project_developer === selectedOrg);
    }

    if (selectedStatus !== 'All Status') {
      const now = new Date();
      if (selectedStatus === 'In Progress') {
        result = result.filter(p => p.project_status === ProjectStatusEnum.UnderDevelopment);
      } else if (selectedStatus === 'Completed') {
        result = result.filter(p => p.project_end_date && new Date(p.project_end_date) < now);
      }
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'Latest') {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      } else if (sortBy === 'Oldest') {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortBy === 'Name (A-Z)') {
        return (a.project_name || '').localeCompare(b.project_name || '');
      }
      return 0;
    });

    return result;
  }, [projects, searchTerm, selectedOrg, selectedStatus, sortBy]);

  const paginatedProjects = useMemo(() => {
    return filteredProjects.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  }, [filteredProjects, page]);

  const handleProjectClick = (projectId: string) => {
    startTransition(() => {
      navigate(`/admin/project/${projectId}`);
    });
  };

  const countryMap = useMemo(() => {
    const map = new Map<string, string>();
    countryList.forEach(c => map.set(c.countryCode2, c.name));
    return map;
  }, []);

  return (
    <Box sx={{ px: 4, pt: 1, pb: 4, backgroundColor: '#0A0A0A', minHeight: '100vh', color: '#FFFFFF' }}>
      {/* Header & Title Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Projects</Typography>
          <Typography sx={{ color: '#999', fontSize: '14px' }}>
            Manage all organization projects and their key information.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsProjectDialogOpen(true)}
          sx={{
            bgcolor: '#10B981',
            color: '#FFF',
            fontWeight: 700,
            fontSize: '15px',
            px: 4,
            py: 1.5,
            borderRadius: '14px',
            textTransform: 'none',
            boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.39)',
            '&:hover': {
              bgcolor: '#059669',
              boxShadow: '0 6px 20px rgba(5, 150, 105, 0.23)',
            }
          }}
        >
          New Project
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#111', color: '#FFF', borderRadius: '12px', p: 1, border: '1px solid #222' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, backgroundColor: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px' }}>
                <FolderIcon sx={{ color: '#10B981', fontSize: 32 }} />
              </Box>
              <Box>
                <Typography sx={{ color: '#999', fontSize: 14 }}>Total Projects</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.total}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#111', color: '#FFF', borderRadius: '12px', p: 1, border: '1px solid #222' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, backgroundColor: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px' }}>
                <HourglassIcon sx={{ color: '#F59E0B', fontSize: 32 }} />
              </Box>
              <Box>
                <Typography sx={{ color: '#999', fontSize: 14 }}>In Progress</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.inProgress}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#111', color: '#FFF', borderRadius: '12px', p: 1, border: '1px solid #222' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px' }}>
                <CheckCircleIcon sx={{ color: '#3B82F6', fontSize: 32 }} />
              </Box>
              <Box>
                <Typography sx={{ color: '#999', fontSize: 14 }}>Completed</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.completed}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search & Filter Bar */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search projects..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            flexGrow: 1,
            maxWidth: 300,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#151515',
              color: '#FFF',
              borderRadius: '8px',
              '& fieldset': { borderColor: '#333' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#666', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <Button 
          variant="outlined" 
          endIcon={<KeyboardArrowDownIcon />} 
          onClick={(e) => setOrgAnchorEl(e.currentTarget)}
          sx={{ borderColor: '#333', color: '#FFF', textTransform: 'none', borderRadius: '8px' }}
        >
          {selectedOrg}
        </Button>
        <Menu
          anchorEl={orgAnchorEl}
          open={Boolean(orgAnchorEl)}
          onClose={() => setOrgAnchorEl(null)}
          PaperProps={{ sx: { backgroundColor: '#151515', color: '#FFF', border: '1px solid #333' } }}
        >
          {organisations.map(org => (
            <MenuItem key={org} onClick={() => { setSelectedOrg(org); setOrgAnchorEl(null); }}>{org}</MenuItem>
          ))}
        </Menu>

        <Button 
          variant="outlined" 
          endIcon={<KeyboardArrowDownIcon />} 
          onClick={(e) => setStatusAnchorEl(e.currentTarget)}
          sx={{ borderColor: '#333', color: '#FFF', textTransform: 'none', borderRadius: '8px' }}
        >
          {selectedStatus}
        </Button>
        <Menu
          anchorEl={statusAnchorEl}
          open={Boolean(statusAnchorEl)}
          onClose={() => setStatusAnchorEl(null)}
          PaperProps={{ sx: { backgroundColor: '#151515', color: '#FFF', border: '1px solid #333' } }}
        >
          {['All Status', 'In Progress', 'Completed'].map(status => (
            <MenuItem key={status} onClick={() => { setSelectedStatus(status); setStatusAnchorEl(null); }}>{status}</MenuItem>
          ))}
        </Menu>

        <Button 
          variant="outlined" 
          startIcon={<FilterIcon />} 
          onClick={() => { setSelectedOrg('All Organisations'); setSelectedStatus('All Status'); setSearchTerm(''); }}
          sx={{ borderColor: '#333', color: '#FFF', textTransform: 'none', borderRadius: '8px' }}
        >
          Reset Filters
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', backgroundColor: '#151515', borderRadius: '8px', p: 0.5, border: '1px solid #333' }}>
          <IconButton
            size="small"
            onClick={() => setViewMode('grid')}
            sx={{ color: viewMode === 'grid' ? '#10B981' : '#666', backgroundColor: viewMode === 'grid' ? 'rgba(16, 185, 129, 0.1)' : 'transparent', borderRadius: '6px' }}
          >
            <GridViewIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setViewMode('list')}
            sx={{ color: viewMode === 'list' ? '#10B981' : '#666', backgroundColor: viewMode === 'list' ? 'rgba(16, 185, 129, 0.1)' : 'transparent', borderRadius: '6px' }}
          >
            <ListIcon fontSize="small" />
          </IconButton>
        </Box>

        <Button 
          variant="outlined" 
          onClick={(e) => setSortAnchorEl(e.currentTarget)}
          sx={{ borderColor: '#333', color: '#FFF', textTransform: 'none', borderRadius: '8px' }}
        >
          Sort by: {sortBy}
        </Button>
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={() => setSortAnchorEl(null)}
          PaperProps={{ sx: { backgroundColor: '#151515', color: '#FFF', border: '1px solid #333' } }}
        >
          {['Latest', 'Oldest', 'Name (A-Z)'].map(option => (
            <MenuItem key={option} onClick={() => { setSortBy(option); setSortAnchorEl(null); }}>{option}</MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Project Display */}
      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {paginatedProjects.map((project) => (
            <Grid item xs={12} md={6} lg={4} key={project.project_id}>
              <ProjectCard project={project} onClick={() => handleProjectClick(project.project_id)} countryName={countryMap.get(project.country || '')} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Table Header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr 0.8fr 100px 100px 120px 120px 150px',
              px: 2,
              py: 1,
              borderBottom: '1px solid #222',
              backgroundColor: '#0A0A0A',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            {['Name', 'Assigned', 'Developer', 'Type', 'Start', 'End', 'Country', 'Created By', 'Status'].map((header, idx) => (
              <Typography
                key={idx}
                variant="caption"
                sx={{
                  color: idx === 0 ? '#EAB308' : '#999',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  pr: 2,
                }}
              >
                {header} {idx === 0 && <span style={{ fontSize: 10 }}>▲</span>}
              </Typography>
            ))}
          </Box>
          <Box sx={{ minHeight: '400px' }}>
            {paginatedProjects.map((project) => (
              <ProjectRow key={project.project_id} project={project} onClick={() => handleProjectClick(project.project_id)} countryName={countryMap.get(project.country || '')} />
            ))}
          </Box>
        </Box>
      )}

      {/* Pagination */}
      <Box sx={{ mt: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ color: '#666' }}>
          {filteredProjects.length > 0 ? (
            `Showing ${(page - 1) * rowsPerPage + 1} to ${Math.min(page * rowsPerPage, filteredProjects.length)} of ${filteredProjects.length} projects`
          ) : (
            'No projects found'
          )}
        </Typography>
        <Pagination
          count={Math.ceil(filteredProjects.length / rowsPerPage)}
          page={page}
          onChange={(_, v) => setPage(v)}
          sx={{
            '& .MuiPaginationItem-root': { color: '#666', borderColor: '#333' },
            '& .Mui-selected': { backgroundColor: '#10B981 !important', color: '#FFF' },
          }}
        />
      </Box>

      {/* Project Creation Dialog */}
      <ProjectDialog
        open={isProjectDialogOpen}
        onClose={() => setIsProjectDialogOpen(false)}
      />
    </Box>
  );
};

const ProjectCard: React.FC<{ project: ProjectAdminSummary; onClick: () => void; countryName?: string }> = ({ project, onClick, countryName }) => {
  return (
    <Card
      onClick={onClick}
      sx={{
        backgroundColor: '#111',
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid #222',
        cursor: 'pointer',
        transition: 'transform 0.2s, border-color 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: '#10B981',
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ position: 'relative', pt: '56.25%' }}>
        <CardMedia
          component="img"
          image="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?q=80&w=2626&auto=format&fit=crop" // Placeholder
          sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box sx={{ position: 'absolute', top: 12, left: 12 }}>
          {(() => {
            const style = getStatusStyle(project.project_status, project.project_end_date);
            return (
              <Chip
                label={style.label.toUpperCase()}
                size="small"
                sx={{
                  backgroundColor: style.backgroundColor,
                  color: style.color,
                  fontWeight: 700,
                  fontSize: 10,
                  borderRadius: '4px',
                }}
              />
            );
          })()}
        </Box>
        <IconButton sx={{ position: 'absolute', top: 8, right: 8, color: '#FFF' }}>
          <MoreVertIcon />
        </IconButton>
      </Box>
      <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Title clamped to 2 lines — ensures uniform card height regardless of name length */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 2,
            color: '#FFF',
            lineHeight: 1.35,
            minHeight: '2.7em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {project.project_name}
        </Typography>

        {/* Middle section grows to fill space — pushes stats to bottom */}
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BusinessIcon sx={{ color: '#666', fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {project.project_developer || '—'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon sx={{ color: '#666', fontSize: 18, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: '#999' }}>
              {countryName || project.country || '—'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#222', mt: 2, mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ color: '#666', fontSize: 10, fontWeight: 700 }}>SITES</Typography>
            <Typography sx={{ color: '#FFF', fontSize: 20, fontWeight: 700 }}>
              {project.assignment?.project?.site_count ?? '-'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ color: '#666', fontSize: 10, fontWeight: 700 }}>START DATE</Typography>
            <Typography sx={{ color: '#FFF', fontSize: 14, fontWeight: 700 }}>{formatDate(project.project_start_date) || '—'}</Typography>
          </Box>
          <Box>
            <Typography sx={{ color: '#666', fontSize: 10, fontWeight: 700 }}>END DATE</Typography>
            <Typography sx={{ color: '#FFF', fontSize: 14, fontWeight: 700 }}>{formatDate(project.project_end_date) || '—'}</Typography>
          </Box>
          <IconButton
            size="small"
            sx={{
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              color: '#10B981',
              '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.2)' },
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </CardContent>

    </Card>
  );
};

const ProjectRow: React.FC<{ project: ProjectAdminSummary; onClick: () => void; countryName?: string }> = ({ project, onClick, countryName }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr 0.8fr 100px 100px 120px 120px 150px',
        alignItems: 'center',
        py: 2,
        px: 2,
        borderBottom: '1px solid #111',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.01)' },
      }}
    >
      {/* Name */}
      <Typography variant="body2" sx={{ color: '#FFF', fontWeight: 500, pr: 2, fontSize: 13 }}>
        {project.project_name}
      </Typography>

      {/* Assigned */}
      <Typography variant="body2" sx={{ color: '#999', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {project.assignment?.user ? `${project.assignment.user.first_name} ${project.assignment.user.last_name}` : '-'}
      </Typography>

      {/* Name (Developer) */}
      <Typography variant="body2" sx={{ color: '#999', fontSize: 13 }}>
        {project.project_developer || '-'}
      </Typography>

      {/* Type */}
      <Typography variant="body2" sx={{ color: '#999', fontSize: 13 }}>
        {project.project_type || '-'}
      </Typography>

      {/* Start */}
      <Typography variant="body2" sx={{ color: '#999', fontSize: 13 }}>
        {formatDate(project.project_start_date) || '-'}
      </Typography>

      {/* End */}
      <Typography variant="body2" sx={{ color: '#999', fontSize: 13 }}>
        {formatDate(project.project_end_date) || '-'}
      </Typography>

      {/* Country */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ color: '#999', fontSize: 13 }}>{countryName || project.country || '-'}</Typography>
      </Box>

      {/* Created By */}
      <Typography variant="body2" sx={{ color: '#999', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {project.created_by?.first_name || '-'}
      </Typography>

      {/* Status */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        {(() => {
          const style = getStatusStyle(project.project_status, project.project_end_date);
          return (
            <Chip
              label={style.label}
              size="small"
              sx={{
                backgroundColor: style.backgroundColor,
                color: style.color,
                fontWeight: 700,
                fontSize: 10,
                height: 20,
                borderRadius: '10px',
              }}
            />
          );
        })()}
      </Box>
    </Box>
  );
};

export default ProjectList;
